import { mockInsights, type Insight } from '../mocks/insights';
import { isServerBackendEnabled } from './env';
import { fetchStateRowsRequest } from './serverApi';
import { STORAGE_KEYS, readStoredJSON, writeStoredJSON } from './state/appState';

const MAX_INLINE_INSIGHT_ASSET_LENGTH = 120000;
const ALLOWED_INSIGHT_STATUSES: Insight['status'][] = ['published', 'draft', 'pending_review'];

export function buildConsultantInsightsStorageKey(userId: string) {
  return `${STORAGE_KEYS.portalInsightsPrefix}${userId}`;
}

function normalizeInsightAssetUrl(value?: string) {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';

  if (/^data:image\//i.test(trimmed)) {
    return '';
  }

  if (trimmed.length > MAX_INLINE_INSIGHT_ASSET_LENGTH) {
    return '';
  }

  return trimmed;
}

function normalizeStoredInsight(item: Insight): Insight {
  const normalizedStatus = ALLOWED_INSIGHT_STATUSES.includes(item.status) ? item.status : 'draft';
  return {
    ...item,
    status: normalizedStatus,
    coverImageUrl: normalizeInsightAssetUrl(item.coverImageUrl),
    authorPhotoUrl: normalizeInsightAssetUrl(item.authorPhotoUrl),
  };
}

function isLegacyDemoInsight(item: Insight) {
  const matchingDemo = mockInsights.find((demoItem) => demoItem.id === item.id);
  if (!matchingDemo) {
    return false;
  }

  return (
    matchingDemo.title === item.title &&
    matchingDemo.subtitle === item.subtitle &&
    matchingDemo.summary === item.summary &&
    matchingDemo.content === item.content &&
    matchingDemo.author === item.author &&
    matchingDemo.category === item.category &&
    matchingDemo.publishedAt === item.publishedAt &&
    matchingDemo.createdAt === item.createdAt &&
    matchingDemo.readTime === item.readTime
  );
}

export function sanitizeStoredInsights(items: Insight[]) {
  return items
    .map((item) => normalizeStoredInsight(item))
    .filter((item) => !isLegacyDemoInsight(item));
}

export function loadStoredInsights() {
  return sanitizeStoredInsights(readStoredJSON<Insight[]>(STORAGE_KEYS.insights, []));
}

export function loadPublishedInsights() {
  return sortPublishedInsights(loadStoredInsights());
}

export function saveStoredInsights(items: Insight[]) {
  writeStoredJSON(STORAGE_KEYS.insights, sanitizeStoredInsights(items));
}

export function loadConsultantInsights(userId: string) {
  return sanitizeStoredInsights(
    readStoredJSON<Insight[]>(buildConsultantInsightsStorageKey(userId), []),
  ).sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
}

export function saveConsultantInsights(userId: string, items: Insight[]) {
  writeStoredJSON(buildConsultantInsightsStorageKey(userId), sanitizeStoredInsights(items));
}

function sortPublishedInsights(items: Insight[]) {
  return items
    .filter((item) => item.status === 'published')
    .sort((left, right) => new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime());
}

export async function fetchInsightsFromServer() {
  if (!isServerBackendEnabled) {
    return loadStoredInsights();
  }

  const response = await fetchStateRowsRequest([STORAGE_KEYS.insights]);
  const row = response.rows.find((entry) => entry.storageKey === STORAGE_KEYS.insights);
  if (!row) {
    return loadStoredInsights();
  }

  return sanitizeStoredInsights(JSON.parse(row.payloadText) as Insight[]);
}

export async function fetchPublishedInsightsFromServer() {
  return sortPublishedInsights(await fetchInsightsFromServer());
}

export async function syncInsightsFromServer() {
  const parsed = await fetchInsightsFromServer();

  try {
    writeStoredJSON(STORAGE_KEYS.insights, parsed);
  } catch (error) {
    console.warn('Insights could not be persisted to localStorage; using hydrated server data only.', error);
  }

  return parsed;
}

export function sanitizeLegacyInsightsInStorage() {
  if (typeof window === 'undefined') {
    return false;
  }

  const storedRaw = readStoredJSON<Insight[]>(STORAGE_KEYS.insights, []);
  const sanitized = sanitizeStoredInsights(storedRaw);
  const nextPayload = JSON.stringify(sanitized);

  if (window.localStorage.getItem(STORAGE_KEYS.insights) === nextPayload) {
    return false;
  }

  window.localStorage.setItem(STORAGE_KEYS.insights, nextPayload);
  return true;
}
